import { useEffect, useMemo, useRef, useState } from "react";
import { Link, Outlet, createFileRoute, useChildMatches, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { AlertTriangle, ArrowLeft, Loader2 } from "lucide-react";
import { toast } from "sonner";

import { loadCaptchaConfig, loadPublicForm, submitForm, type PublicForm, type PublicFormField } from "@/lib/api";
import { loadHCaptchaScript } from "@/lib/hcaptcha";

type FromPortal = "partner" | "client";

type FormSearch = { return_to_asset?: string; from?: FromPortal };

export const Route = createFileRoute("/$tenantSlug/forms/$formSlug")({
  component: PublicFormPage,
  validateSearch: (search): FormSearch => ({
    return_to_asset:
      typeof search.return_to_asset === "string" && search.return_to_asset
        ? search.return_to_asset
        : undefined,
    from:
      search.from === "partner" || search.from === "client"
        ? (search.from as FromPortal)
        : undefined,
  }),
});

function PublicFormPage() {
  const { tenantSlug, formSlug } = Route.useParams();
  const { return_to_asset, from } = Route.useSearch();
  const childMatches = useChildMatches();
  const hasChild = childMatches.length > 0;
  const { data: form, isLoading, isError, error } = useQuery({
    queryKey: ["public-form", tenantSlug, formSlug] as const,
    queryFn: () => loadPublicForm(tenantSlug, formSlug),
    enabled: !hasChild,
  });

  if (hasChild) {
    return <Outlet />;
  }

  if (isLoading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <Loader2 className="size-5 animate-spin text-slate-400" />
      </div>
    );
  }
  if (isError || !form) {
    return (
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="max-w-md text-center space-y-3">
          <AlertTriangle className="size-8 mx-auto text-slate-400" />
          <h1 className="text-lg font-medium">Form unavailable</h1>
          <p className="text-sm text-slate-500">
            {(error as Error | null)?.message ??
              "This form may have been removed or the URL is no longer valid."}
          </p>
        </div>
      </div>
    );
  }

  return (
    <FormRenderer
      tenantSlug={tenantSlug}
      formSlug={formSlug}
      form={form}
      returnToAsset={return_to_asset}
      fromPortal={from}
    />
  );
}

function PortalBackLink({
  tenantSlug,
  from,
}: {
  tenantSlug: string;
  from: FromPortal | undefined;
}) {
  if (!from) return null;
  const href = `/${encodeURIComponent(tenantSlug)}/${from}`;
  const label = from === "partner" ? "Back to partner portal" : "Back to client portal";
  return (
    <Link
      to={href}
      className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-900 dark:text-slate-400 dark:hover:text-slate-100 transition-colors"
    >
      <ArrowLeft className="size-3" /> {label}
    </Link>
  );
}

// --------------------------------------------------------------------------
// Form renderer
// --------------------------------------------------------------------------

function FormRenderer({
  tenantSlug,
  formSlug,
  form,
  returnToAsset,
  fromPortal,
}: {
  tenantSlug: string;
  formSlug: string;
  form: PublicForm;
  returnToAsset?: string;
  fromPortal?: FromPortal;
}) {
  const navigate = useNavigate();
  const [values, setValues] = useState<Record<string, unknown>>(() => {
    const seed: Record<string, unknown> = {};
    for (const f of form.fields) {
      seed[f.name] = f.type === "checkbox" ? false : "";
    }
    return seed;
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const captchaWidgetRef = useRef<HTMLDivElement | null>(null);
  const widgetIdRef = useRef<number | null>(null);
  const honeypotName = form.honeypot_field || "company_website_url";
  const honeypotRef = useRef<HTMLInputElement | null>(null);
  const idempotencyKey = useMemo(() => randomToken(), []);

  const { data: captchaConfig } = useQuery({
    queryKey: ["captcha-config"] as const,
    queryFn: loadCaptchaConfig,
    enabled: form.captcha_required,
  });

  useEffect(() => {
    if (!form.captcha_required) return;
    if (!captchaConfig?.site_key) return;
    let cancelled = false;
    void loadHCaptchaScript().then(() => {
      if (cancelled) return;
      if (!captchaWidgetRef.current) return;
      if (!window.hcaptcha) return;
      if (widgetIdRef.current !== null) return;
      const prefersDark = window.matchMedia?.("(prefers-color-scheme: dark)").matches;
      widgetIdRef.current = window.hcaptcha.render(captchaWidgetRef.current, {
        sitekey: captchaConfig.site_key!,
        theme: prefersDark ? "dark" : "light",
        callback: (token: string) => setCaptchaToken(token),
        "expired-callback": () => setCaptchaToken(null),
        "error-callback": () => setCaptchaToken(null),
      });
    });
    return () => {
      cancelled = true;
    };
  }, [form.captcha_required, captchaConfig?.site_key]);

  function setField(name: string, value: unknown) {
    setValues((v) => ({ ...v, [name]: value }));
    if (errors[name]) {
      setErrors((e) => {
        const next = { ...e };
        delete next[name];
        return next;
      });
    }
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    const v: Record<string, string> = {};
    for (const f of form.fields) {
      const raw = values[f.name];
      if (f.required && (raw === null || raw === undefined || raw === "" || raw === false)) {
        v[f.name] = `${f.label} is required`;
      }
      if (f.type === "email" && typeof raw === "string" && raw && !raw.includes("@")) {
        v[f.name] = "Looks like that's not an email address";
      }
    }
    if (Object.keys(v).length > 0) {
      setErrors(v);
      return;
    }
    if (form.captcha_required && !captchaToken) {
      toast.error("Complete the captcha to continue");
      return;
    }
    setSubmitting(true);
    try {
      const result = await submitForm(tenantSlug, formSlug, {
        fields: values,
        captcha_token: captchaToken ?? "",
        honeypot: honeypotRef.current?.value ?? "",
        idempotency_key: idempotencyKey,
        portal_kind: fromPortal,
      });
      if (!result.ok) {
        toast.error("Submission could not be completed");
        return;
      }
      const tokens = Array.isArray(result.download_tokens) ? result.download_tokens : [];
      if (returnToAsset && tokens.length > 0) {
        const match = tokens.find((t) => t.asset_slug === returnToAsset);
        const tok = match ?? tokens[0];
        if (tok) {
          await navigate({
            to: "/$tenantSlug/assets/$assetSlug",
            params: { tenantSlug, assetSlug: tok.asset_slug },
            search: { token: tok.token },
          });
          return;
        }
      }
      await navigate({
        to: "/$tenantSlug/forms/$formSlug/thanks",
        params: { tenantSlug, formSlug },
        search: {
          confirmation: result.confirmation_email_sent ? 1 : 0,
          deduped: result.deduped ? 1 : 0,
          ...(fromPortal ? { from: fromPortal } : {}),
        },
      });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Submission failed");
      if (widgetIdRef.current !== null && window.hcaptcha) {
        window.hcaptcha.reset(widgetIdRef.current);
        setCaptchaToken(null);
      }
    } finally {
      setSubmitting(false);
    }
  }

  const primaryColor = form.branding?.primary_color ?? "#08142b";

  return (
    <div className="flex-1 flex items-start justify-center p-4 sm:p-8 bg-slate-50 dark:bg-slate-950">
      <div className="w-full max-w-xl bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-slate-200 dark:border-slate-800 p-6 sm:p-8 space-y-6">
        <div>
          <PortalBackLink tenantSlug={tenantSlug} from={fromPortal} />
        </div>
        <header className="space-y-1.5">
          {form.branding?.logo_url ? (
            <img src={form.branding.logo_url} alt="" className="h-8 mb-2" />
          ) : null}
          <h1 className="text-xl font-medium text-slate-900 dark:text-slate-100">
            {form.name}
          </h1>
          {form.description ? (
            <p className="text-sm text-slate-600 dark:text-slate-400 whitespace-pre-line">
              {form.description}
            </p>
          ) : null}
        </header>

        <form onSubmit={onSubmit} className="space-y-4" autoComplete="on" noValidate>
          {form.fields.map((f) => (
            <FieldRenderer
              key={f.name}
              field={f}
              value={values[f.name]}
              error={errors[f.name]}
              onChange={(v) => setField(f.name, v)}
            />
          ))}

          {/* honeypot — visually hidden, bots fill, humans skip */}
          <div className="hidden" aria-hidden="true">
            <label>
              {honeypotName}
              <input
                ref={honeypotRef}
                type="text"
                name={honeypotName}
                tabIndex={-1}
                autoComplete="off"
              />
            </label>
          </div>

          {form.captcha_required ? (
            <div className="pt-2">
              <div ref={captchaWidgetRef} />
              {!captchaConfig?.site_key ? (
                <p className="text-xs text-amber-600">
                  Captcha not configured. Submissions are temporarily paused.
                </p>
              ) : null}
            </div>
          ) : null}

          <button
            type="submit"
            disabled={submitting}
            className="w-full rounded-md px-4 py-2.5 text-sm font-medium text-white disabled:opacity-60 transition"
            style={{ background: primaryColor }}
          >
            {submitting ? (
              <span className="inline-flex items-center gap-2">
                <Loader2 className="size-4 animate-spin" /> Sending
              </span>
            ) : (
              "Submit"
            )}
          </button>
        </form>

        <p className="text-xs text-slate-400 text-center pt-2 border-t border-slate-100 dark:border-slate-800">
          Powered by ActionaryCRM
        </p>
      </div>
    </div>
  );
}

// --------------------------------------------------------------------------
// Field renderer
// --------------------------------------------------------------------------

function FieldRenderer({
  field,
  value,
  error,
  onChange,
}: {
  field: PublicFormField;
  value: unknown;
  error?: string;
  onChange: (next: unknown) => void;
}) {
  if (field.type === "hidden") {
    return <input type="hidden" name={field.name} value={String(value ?? "")} />;
  }
  const baseInput =
    "block w-full rounded-md border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-950 px-3 py-2 text-sm placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-900 dark:focus:ring-slate-300 transition";
  const errorStyle = error ? "border-red-400 focus:ring-red-500" : "";

  function Label() {
    return (
      <label className="block text-sm font-medium text-slate-800 dark:text-slate-200">
        {field.label}
        {field.required ? <span className="text-red-500 ml-0.5">*</span> : null}
      </label>
    );
  }

  function Helper() {
    if (error) return <p className="text-xs text-red-600">{error}</p>;
    if (field.helper_text)
      return <p className="text-xs text-slate-500">{field.helper_text}</p>;
    return null;
  }

  if (field.type === "checkbox") {
    return (
      <div className="space-y-1">
        <label className="inline-flex items-start gap-2 text-sm">
          <input
            type="checkbox"
            checked={Boolean(value)}
            onChange={(e) => onChange(e.target.checked)}
            className="mt-0.5"
          />
          <span>
            {field.label}
            {field.required ? <span className="text-red-500 ml-0.5">*</span> : null}
          </span>
        </label>
        <Helper />
      </div>
    );
  }

  if (field.type === "textarea") {
    return (
      <div className="space-y-1">
        <Label />
        <textarea
          name={field.name}
          rows={4}
          value={String(value ?? "")}
          onChange={(e) => onChange(e.target.value)}
          placeholder={field.placeholder}
          required={field.required}
          className={`${baseInput} ${errorStyle}`}
        />
        <Helper />
      </div>
    );
  }

  if (field.type === "select" || field.type === "multi_select") {
    const opts = flattenFieldOptions(field.options);
    if (field.type === "multi_select") {
      const selected = Array.isArray(value) ? (value as string[]) : [];
      return (
        <div className="space-y-1">
          <Label />
          <div className={`${baseInput} ${errorStyle} space-y-1`}>
            {opts.map((o) => (
              <label key={o} className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={selected.includes(o)}
                  onChange={(e) => {
                    const next = e.target.checked
                      ? [...selected, o]
                      : selected.filter((x) => x !== o);
                    onChange(next);
                  }}
                />
                <span>{o}</span>
              </label>
            ))}
          </div>
          <Helper />
        </div>
      );
    }
    return (
      <div className="space-y-1">
        <Label />
        <select
          name={field.name}
          value={String(value ?? "")}
          onChange={(e) => onChange(e.target.value)}
          required={field.required}
          className={`${baseInput} ${errorStyle}`}
        >
          <option value="">Pick one</option>
          {opts.map((o) => (
            <option key={o} value={o}>
              {o}
            </option>
          ))}
        </select>
        <Helper />
      </div>
    );
  }

  const inputType =
    field.type === "email"
      ? "email"
      : field.type === "phone"
        ? "tel"
        : field.type === "number"
          ? "number"
          : field.type === "date"
            ? "date"
            : "text";
  return (
    <div className="space-y-1">
      <Label />
      <input
        type={inputType}
        name={field.name}
        value={String(value ?? "")}
        onChange={(e) =>
          onChange(field.type === "number" ? Number(e.target.value) : e.target.value)
        }
        placeholder={field.placeholder}
        required={field.required}
        className={`${baseInput} ${errorStyle}`}
        autoComplete={inferAutocomplete(field)}
      />
      <Helper />
    </div>
  );
}

function inferAutocomplete(f: PublicFormField): string {
  if (f.type === "email") return "email";
  if (f.type === "phone") return "tel";
  const n = f.name.toLowerCase();
  if (n.includes("first") && n.includes("name")) return "given-name";
  if (n.includes("last") && n.includes("name")) return "family-name";
  if (n.includes("company")) return "organization";
  if (n === "name") return "name";
  return "on";
}

function randomToken(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) return crypto.randomUUID();
  return Math.random().toString(36).slice(2) + Date.now().toString(36);
}

/**
 * Coerce a select / multi_select ``options`` array to flat strings.
 * The canonical shape is `string[]` — a flat list — and the backend
 * normaliser at `_validate_form_payload` enforces that on write. This
 * client-side helper is defence against rows written before the
 * server-side normaliser landed (form-editor histories that shipped
 * `{label, value}` objects, older MCP tool versions, etc.) so a bad
 * row renders as a functional select rather than crashing React with
 * "Objects are not valid as a React child".
 */
function flattenFieldOptions(raw: unknown): string[] {
  if (!Array.isArray(raw)) return [];
  const out: string[] = [];
  for (const item of raw) {
    let text = "";
    if (typeof item === "string") {
      text = item.trim();
    } else if (item && typeof item === "object") {
      const rec = item as Record<string, unknown>;
      const candidate = rec.label ?? rec.value ?? rec.name ?? "";
      text = String(candidate).trim();
    } else if (item != null) {
      text = String(item).trim();
    }
    if (text) out.push(text);
  }
  return out;
}
